import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

export interface AlertContext {
  alertId: number
  timestamp: number
  glucoseValue?: number
  alertType: string
  severity: string
  title: string
}

/**
 * Custom hook to handle alert context for navigation and chart highlighting
 * The alert comes from the URL; sessionStorage only supplies severity/title
 * for that same alert (matched by alertId).
 */
export const useAlertContext = () => {
  const location = useLocation()
  const [alertContext, setAlertContext] = useState<AlertContext | null>(null)

  useEffect(() => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(location.search)
    const alertId = urlParams.get('alertId')
    const alertTimestamp = urlParams.get('alertTimestamp')
    const alertType = urlParams.get('alertType')
    const alertGlucose = urlParams.get('alertGlucose')

    if (alertId && alertTimestamp && alertType) {
      const context: AlertContext = {
        alertId: parseInt(alertId, 10),
        timestamp: parseInt(alertTimestamp, 10),
        glucoseValue: alertGlucose ? parseFloat(alertGlucose) : undefined,
        alertType,
        severity: 'unknown', // Will be updated from sessionStorage if available
        title: '', // the banner renders pos.alerts.fallback_title when no title was stashed
      }

      // Borrow severity/title from sessionStorage only when it describes THIS
      // alert — the stash carries no pet id, so any other alert's would be
      // another patient's (P71-9).
      try {
        const storedContext = sessionStorage.getItem('currentAlertContext')
        if (storedContext) {
          const stored = JSON.parse(storedContext)
          if (stored?.alertId === context.alertId) {
            context.severity = stored.severity || context.severity
            context.title = stored.title || context.title
          }
        }
      } catch (error) {
        console.warn('Failed to parse stored alert context:', error)
      }

      setAlertContext(context)
    } else {
      // No alert in the URL means no alert context. There used to be a
      // sessionStorage fallback here, but both alert-click handlers put the
      // alert in the URL, so the fallback only ever resurrected an EARLIER
      // click — and, carrying no pet id, painted pet A's alert banner onto
      // any pet page opened afterwards (P71-9).
      setAlertContext(null)
    }
  }, [location.search])

  const clearAlertContext = () => {
    setAlertContext(null)
    sessionStorage.removeItem('currentAlertContext')
  }

  return {
    alertContext,
    clearAlertContext,
    hasAlertContext: !!alertContext,
  }
}
